import {ALL, BIND, IF, REFLECT} from "./directives";
import {Utility} from "./utility";
import {Features} from "./api";
import {TemplateSyntax} from "./template-syntax";
import {errorHandler} from "../errors/errorHandler";

export const Reactivity = {

	/**
	 * Will start the reactivity engine
	 * @param {Document} template
	 */
	initialize: async (template) => {

		// search through the template for any directives
		await Reactivity.searchTemplateForDirectives(template);

		// insert the reactive values into their placeholders
		Reactivity.insertReactiveValues(template);

	},

	/**
	 * Returns what a reactive element binding should store
	 * @returns {{elementParent: boolean, bindingType: string, isRct: boolean, elementBinding: string, contentEditable: boolean, attributeValue: boolean, containsMultiBindings: boolean, isDef: boolean, rawHTML: string, elementIndex: boolean, bindDirectiveType: string, elementBindings: [], animationFunction: boolean, reference: string, animationOptions: boolean, twoWayBinding: string, elementReference: boolean, attributeName: string}}
	 */
	reactiveElement: () => {
		return {
			elementReference: false,
			elementBinding: '',
			elementBindings: [],
			elementIndex: false,
			elementParent: false,
			twoWayBinding: '',
			contentEditable: false,
			reference: '',
			bindingType: '',
			rawHTML: '',
			isDef: false,
			isRct: false,
			attributeName: '',
			attributeValue: false,
			bindDirectiveType: '',
			animationFunction: false,
			animationOptions: false,
			containsMultiBindings: false
		};
	},

	/**
	 * Will search the template supplied for custom directives and replace with a key
	 * @param {Document} template
	 */
	searchTemplateForDirectives: (template) => {
		return new Promise(resolve => {
			let allElements = template.body.getElementsByTagName("*");
			for (let element = 0; element < allElements.length; element++) {
				let ele = allElements[element];

				Object.entries(ALL).forEach(directive => {

					let key = directive[0];
					let value = directive[1];

					if (ele.hasAttribute(key)) {
						// get attr value
						let attrVal = ele.attributes[key].value;

						// set new attribute
						ele.removeAttribute(key)
						ele.setAttribute(value, attrVal);

					}
				});

			}

			resolve();
		});
	},

	/**
	 * Searches for different types of interpolation and inserts the values
	 * @param {Document} template
	 */
	insertReactiveValues: (template) => {
		let foundInterpolation = false;

		let allElements = template.body.getElementsByTagName("*");

		for (let element = 0; element < allElements.length; element++) {
			let ele = allElements[element];
			let REF = Utility.getRandomString(7, "LOWERCASE");

			// this gets all the Bind elements with interpolation
			TemplateSyntax.BindDirectives(ele, REF);

			// this checks if an element has an animation applied to it
			TemplateSyntax.AnimationDirectives(ele, REF);

			// this gets all the If elements with interpolation
			TemplateSyntax.IfDirectives(ele, REF);

			// this gets all the elements with interpolation in the attributes
			TemplateSyntax.getAllAttributeInterpolation(ele, REF);

			// this gets all the elements with interpolation inside
			if (ele.children.length === 0 && ele.localName !== 'style' && ele.localName !== 'code') {
				TemplateSyntax.getAllHtmlInterpolation(ele, REF);
			}

		}

		return foundInterpolation;
	},

	/**
	 * Will setup an event to watch if the DOM is updated
	 */
	setupDomMutationWatcher: () => {
		const mutationObserver = new MutationObserver((mutations) => {
			// search through the template for any directives
			Reactivity.searchTemplateForDirectives(document);

			// insert the reactive values into their placeholders
			Reactivity.insertReactiveValues(document);
			debugger;
			// apply any updates to the template needed
			Reactivity.ApplyDomUpdates({
				template: document,
				loadedVM: Features.VM,
				propertyToUpdate: false,
			});

			// mutationObserver.disconnect();
		});

		mutationObserver.observe(document.documentElement, {
			attributes: false,
			characterData: false,
			childList: true,
			subtree: true,
			attributeOldValue: false,
			characterDataOldValue: false
		});

	},

	/**
	 * Will be called each time a reactive property is updated or changed
	 * @param {{template: Document, loadedVM: Object, propertyToUpdate: false, isDef: false }} options
	 * @constructor
	 */
	ApplyDomUpdates: (options) => {
		// updating a property after initial render
		if (options.propertyToUpdate !== false) {
			// get the DOM Binding needing update
			const Bindings = Utility.getDOMBindingNeedingUpdate(options);

			// iterate over all bindings using the property to update
			if (Bindings.length !== 0) Bindings.forEach(DOMBinding => {
				// check if the Binding contains multiple binding
				if (DOMBinding.containsMultiBindings) {
					DOMBinding.elementBindings.forEach(Binding => {
						if (Binding.isDef) {
							if (options.loadedVM[Binding.bindingName] !== undefined) {
								Reactivity.handleReactiveVariables({
									DOMBinding,
									loadedVM: options.loadedVM,
									propertyToUpdate: Binding.bindingName
								});
							}
							else {
								errorHandler.New({
									label: 'Syntax Error',
									message: `The property you are trying to access <b>{${Binding.bindingName}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
								});
							}
						}
						else if (DOMBinding.isRct) {
							const nestedProperty = Binding.bindingName.split('.');
							if (options.loadedVM[nestedProperty[0]]) {
								const data = Utility.getNestedProperty(options.loadedVM, Binding.bindingName);
								if (data !== undefined) {
									Reactivity.handleReactiveObjects({
										DOMBinding,
										loadedVM: options.loadedVM,
										propertyToUpdate: Binding.bindingName
									});
								}
								else {
									errorHandler.New({
										label: 'Syntax Error',
										message: `The property you are trying to access <b>{${Binding.bindingName}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
									});
								}
							}
						}
					});
				}
				// if (propertyToUpdate) is not a nested property and defined with the Def() Method
				else if (DOMBinding.elementBinding === options.propertyToUpdate) {
					if (options.isDef && options.loadedVM[options.propertyToUpdate]) {
						Reactivity.handleReactiveVariables({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: options.propertyToUpdate
						});
					}
				}
				// if there are multiple bindings for this element
				else if (DOMBinding.elementBindings.length > 0) {
					if (options.isDef) {
						Reactivity.handleReactiveVariables({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: options.propertyToUpdate
						});
					}
					else if (DOMBinding.isRct) {
						Reactivity.handleReactiveObjects({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: DOMBinding.propertyToUpdate
						});
					}
				}
				// if binding is an expression
				else if (DOMBinding.isExpression) {
					Reactivity.handleIfDirectives({
						DOMBinding: DOMBinding,
						loadedVM: options.loadedVM,
						propertyToUpdate: options.propertyToUpdate
					})
				}
				// if (propertyToUpdate) is a nested property defined with the Reactive() method
				else {
					const nestedProperty = DOMBinding.elementBinding.split('.');
					if (nestedProperty[nestedProperty.length - 1] === options.propertyToUpdate) {
						Reactivity.handleReactiveObjects({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: DOMBinding.elementBinding
						});
					}
				}
			});
		}
		// updating view Template before initial render
		else {
			// First DOM update to set the initial values
			$ActiveJS.DOMBindings.forEach(DOMBinding => {
				// check if the Binding contains multiple binding
				if (DOMBinding.containsMultiBindings) {
					DOMBinding.elementBindings.forEach(Binding => {
						if (Binding.isDef) {
							if (options.loadedVM[Binding.bindingName] !== undefined) {
								Reactivity.handleReactiveVariables({
									DOMBinding,
									loadedVM: options.loadedVM,
									propertyToUpdate: Binding.bindingName
								});
							}
							else {
								errorHandler.New({
									label: 'Syntax Error',
									message: `The property you are trying to access <b>{${Binding.bindingName}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
								});
							}
						}
						else if (DOMBinding.isRct) {
							const nestedProperty = Binding.bindingName.split('.');
							if (options.loadedVM[nestedProperty[0]]) {
								const data = Utility.getNestedProperty(options.loadedVM, Binding.bindingName);
								if (data !== undefined) {
									Reactivity.handleReactiveObjects({
										DOMBinding,
										loadedVM: options.loadedVM,
										propertyToUpdate: Binding.bindingName
									});
								}
								else {
									errorHandler.New({
										label: 'Syntax Error',
										message: `The property you are trying to access <b>{${Binding.bindingName}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
									});
								}
							}
						}
					});
				}
				// check if the binding is for a property defined with the Def() method
				else if (DOMBinding.isDef) {
					if (options.loadedVM[DOMBinding.elementBinding] !== undefined) {
						Reactivity.handleReactiveVariables({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: DOMBinding.elementBinding
						});
					}
					else {
						errorHandler.New({
							label: 'Syntax Error',
							message: `The property you are trying to access <b>{${DOMBinding.elementBinding}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
						});
					}
				}
				// check if the binding is for a property defined with the Reactive() method
				else if (DOMBinding.isRct) {
					const nestedProperty = DOMBinding.elementBinding.split('.');
					if (options.loadedVM[nestedProperty[0]]) {
						const data = Utility.getNestedProperty(options.loadedVM, DOMBinding.elementBinding);
						if (data !== undefined) {
							Reactivity.handleReactiveObjects({
								DOMBinding,
								loadedVM: options.loadedVM,
								propertyToUpdate: DOMBinding.elementBinding
							});
						}
						else {
							errorHandler.New({
								label: 'Syntax Error',
								message: `The property you are trying to access <b>{${DOMBinding.elementBinding}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
							});
						}
					}
				}
				// check if the binding is for a normal property/variable
				else {
					if (options.loadedVM[DOMBinding.elementBinding] !== undefined) {
						Reactivity.handleStandardExposedData({
							DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: DOMBinding.elementBinding
						})
					}
					else if (DOMBinding.isExpression && DOMBinding.bindingType === 'IF') {
						Reactivity.handleIfDirectives({
							DOMBinding: DOMBinding,
							loadedVM: options.loadedVM,
							propertyToUpdate: options.propertyToUpdate
						});
					}
					else {
						console.dir(DOMBinding);
						errorHandler.New({
							label: 'Syntax Error',
							message: `The property you are trying to access <b>{${DOMBinding.elementBinding}}</b>, has not been exposed to the DOM ${DOMBinding.elementReference.outerHTML}`
						});
					}
				}
			});
		}

	},

	/**
	 * Updates all data in the DOM which is not reactive
	 * @param {{DOMBinding: Object, loadedVM: Object, propertyToUpdate: String}} options
	 */
	handleStandardExposedData: (options) => {
		// check what type of binding it is
		switch (options.DOMBinding.bindingType) {
			case "HTML":
				Utility.replaceDynamicValues(options.DOMBinding, options.loadedVM[options.DOMBinding.elementBinding]);
				break;
			case "ATTRIBUTE":
				if (options.DOMBinding.attributeName == "disabled") options.DOMBinding.elementReference.disabled = options.loadedVM[options.DOMBinding.elementBinding];
				else if (options.DOMBinding.attributeName == "checked") options.DOMBinding.elementReference.checked = options.loadedVM[options.DOMBinding.elementBinding];
				else options.DOMBinding.elementReference.attributes[options.DOMBinding.attributeName].value = options.loadedVM[options.DOMBinding.elementBinding];
				break;
			case "BIND":
				Reactivity.handleBindDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "IF":
				Reactivity.handleIfDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
		}
	},

	/**
	 * Will decide how to get the value for a property that used Def()
	 * @param {{DOMBinding: Object, loadedVM: Object, propertyToUpdate: String}} options
	 */
	handleReactiveVariables: (options) => {
		// check what type of binding it is
		switch (options.DOMBinding.bindingType) {
			case "HTML":
				if (!options.DOMBinding.containsMultiBindings) Utility.replaceDynamicValues(options.DOMBinding, options.loadedVM[options.propertyToUpdate].value);
				else Reactivity.handleMultipleHTMLBindings({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "ATTRIBUTE":
				if (!options.DOMBinding.containsMultiBindings) {
					if (options.DOMBinding.attributeName == "disabled") options.DOMBinding.elementReference.disabled = options.loadedVM[options.propertyToUpdate].value;
					else if (options.DOMBinding.attributeName == "checked") options.DOMBinding.elementReference.checked = options.loadedVM[options.propertyToUpdate].value;
					else options.DOMBinding.elementReference.attributes[options.DOMBinding.attributeName].value = options.loadedVM[options.propertyToUpdate].value;
				}
				else Reactivity.handleMultipleAttributeBindings({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "BIND":
				Reactivity.handleBindDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "IF":
				Reactivity.handleIfDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
		}
	},

	/**
	 * Sets nested reactive properties
	 * @param {{DOMBinding: Object, loadedVM: Object, propertyToUpdate: String, initialRender: Boolean}} options
	 */
	handleReactiveObjects: (options) => {

		// check what type of binding it is
		switch (options.DOMBinding.bindingType) {
			case "HTML":
				if (!options.DOMBinding.containsMultiBindings) Utility.replaceDynamicValues(options.DOMBinding, Utility.getNestedProperty(options.loadedVM, options.DOMBinding.elementBinding));
				else Reactivity.handleMultipleHTMLBindings({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "ATTRIBUTE":
				if (!options.DOMBinding.containsMultiBindings) {
					if (options.DOMBinding.attributeName == "disabled") options.DOMBinding.elementReference.disabled = Utility.getNestedProperty(options.loadedVM, options.DOMBinding.attributeValue);
					else if (options.DOMBinding.attributeName == "checked") options.DOMBinding.elementReference.checked = Utility.getNestedProperty(options.loadedVM, options.DOMBinding.attributeValue);
					else options.DOMBinding.elementReference.attributes[options.DOMBinding.attributeName].value = Utility.getNestedProperty(options.loadedVM, options.DOMBinding.attributeValue);
				}
				else Reactivity.handleMultipleAttributeBindings({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "BIND":
				Reactivity.handleBindDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
			case "IF":
				Reactivity.handleIfDirectives({DOMBinding: options.DOMBinding, loadedVM: options.loadedVM});
				break;
		}
	},

	/**
	 * Updates the values for multiple HTML bindings in one element
	 * @param {{DOMBinding: Object, loadedVM: Object}} options
	 */
	handleMultipleHTMLBindings: (options) => {
		let preparedHTML = '';

		// look through all bindings and build up the values for each
		options.DOMBinding.elementBindings.forEach(binding => {
			let elementValue;
			const rgx = new RegExp('{'+binding.bindingName+'}', "g");

			// define the value to be put inside element
			if (binding.isDef) elementValue = options.loadedVM[binding.bindingName].value;
			else if (binding.isRct) elementValue = Utility.getNestedProperty(options.loadedVM, binding.bindingName);
			else elementValue = options.loadedVM[binding.bindingName];

			// if (preparedHTML) is empty
			if (preparedHTML === '') {
				preparedHTML = options.DOMBinding.rawHTML.replace(rgx, elementValue);
			}
			else {
				preparedHTML = preparedHTML.replace(rgx, elementValue);
			}
		});

		// set the innerHTML of the element
		options.DOMBinding.elementReference.innerHTML = preparedHTML;
	},

	/**
	 * Updates the values for multiple ATTRIBUTE bindings in one element
	 * @param {{DOMBinding: Object, loadedVM: Object}} options
	 */
	handleMultipleAttributeBindings: (options) => {
		let preparedAttribute = '';

		// look through all bindings and build up the values for each
		options.DOMBinding.elementBindings.forEach(binding => {
			let elementValue;
			const rgx = new RegExp('{'+binding.bindingName+'}', "g");

			// define the value to be put inside element
			if (binding.isDef) elementValue = options.loadedVM[binding.bindingName].value;
			else if (binding.isRct) elementValue = Utility.getNestedProperty(options.loadedVM, binding.bindingName);
			else elementValue = options.loadedVM[binding.bindingName];

			// if (preparedHTML) is empty
			if (preparedAttribute === '') {
				preparedAttribute = binding.rawAttribute.replace(rgx, elementValue);
			}
			else {
				preparedAttribute = preparedAttribute.replace(rgx, elementValue);
			}

			// set the attribute value of the element
			options.DOMBinding.elementReference.attributes[binding.attributeName].value = preparedAttribute;
		});
	},

	/**
	 * Updates element with the If directive
	 * @param {{DOMBinding: Object, loadedVM: Object, propertyToUpdate: String}} options
	 */
	handleIfDirectives: (options) => {
		// get value
		let parsedValue;
		const rawValue = options.DOMBinding.elementBinding;
		
		// check if the value is an expresion
		if (options.DOMBinding.isExpression) {
			const expresion = rawValue.replace(/#/g, "");
			parsedValue = eval("(" + expresion + ")");
		}
		else {
			// check if value is nested or not
			if (Utility.isNestedProperty(rawValue)) parsedValue = Utility.getNestedProperty(Features.VM, rawValue);
			else parsedValue = Features.VM[rawValue].value;
		}

		// if element does not have a sibling with an else directive and is a truthy value
		if (!options.DOMBinding.elementSibling && parsedValue) {
			Utility.toggleElement(parsedValue, options.DOMBinding.elementReference, options.DOMBinding);
		}
		else {
			// if the value of the VM property is truthy
			if (parsedValue) {
				Utility.toggleElement(parsedValue, options.DOMBinding.elementReference, options.DOMBinding);
				Utility.toggleElement(false, options.DOMBinding.elementSibling, options.DOMBinding);
			}
			else {
				Utility.toggleElement(false, options.DOMBinding.elementReference, options.DOMBinding);
				if (options.DOMBinding.elementSibling) Utility.toggleElement(true, options.DOMBinding.elementSibling, options.DOMBinding);
			}
		}

		// check for element animation if the initial render is over
		if ($ActiveJS.initialRenderFinished) Reactivity.checkForElementAnimations(options.DOMBinding.elementReference, true);
	},

	/**
	 * Handles all updates for elements with the Bind directive
	 * @param {{DOMBinding: Object, loadedVM: Object}} options
	 */
	handleBindDirectives: (options) => {
		if (options.DOMBinding.isDef) {
			// handle updating bind value for a class on the element
			if (options.DOMBinding.twoWayBinding === "class") {
				// reset classes
				options.DOMBinding.elementReference.className = "";

				// apply classes to element
				const classes = options.loadedVM[options.DOMBinding.elementBinding].value.split(" ");
				classes.forEach(className => {if (className !== "") options.DOMBinding.elementReference.classList.add(className)});
			}
			// handle everything else
			else {
				options.DOMBinding.elementReference[options.DOMBinding.twoWayBinding] = options.loadedVM[options.DOMBinding.elementBinding].value;
			}
		}
		else if (options.DOMBinding.isRct) {

			// handle updating bind value for a class on the element
			if (options.DOMBinding.twoWayBinding === "class") {
				// reset classes
				options.DOMBinding.elementReference.className = "";

				// apply classes to element
				const classes = Utility.getNestedProperty(options.loadedVM, options.DOMBinding.elementBinding).split(" ");
				classes.forEach(className => {if (className !== "") options.DOMBinding.elementReference.classList.add(className)});
			}
			// handle everything else
			else {
				options.DOMBinding.elementReference[options.DOMBinding.twoWayBinding] = Utility.getNestedProperty(options.loadedVM, options.DOMBinding.elementBinding);
			}
		}
	},

	/**
	 * Runs through all elements with an animation and applies them
	 * @constructor
	 */
	checkForElementAnimations: async (Element, fromIf=false) => {
		// if the update is coming from the if directive
		if (fromIf) {
			// get the animation for the element
			const animation = $ActiveJS.DOMAnimations.filter(animationRef => {
				if (animationRef.elementReference.isSameNode(Element)) return animationRef;
			})[0];

			// if animation found, animate it
			if (animation) {
				if (Element.animate) Features.VM[animation.animationFunction](Element, animation.animationOptions);
				else console.warn(`WARNING :: The animation ${animation.animationFunction}() is an unsuported feature in current browser`);
			}

			// if the element coming from the IF has children
			if (Element.children.length > 0) {

				for (let i = 0; i < Element.children.length; i++) {
					const child = Element.children[i];
					// get the animation for the element
					const animation = $ActiveJS.DOMAnimations.filter(animationRef => {
						if (animationRef.elementReference.isSameNode(child)) return animationRef;
					})[0];

					// if animation found, animate it
					if (animation) {
						if (Element.animate) Features.VM[animation.animationFunction](child, animation.animationOptions);
						else console.warn(`WARNING :: The animation ${animation.animationFunction}() is an unsuported feature in current browser`);
					}
				}
			}
		}
		// if it just the initial render process
		else {
			$ActiveJS.DOMAnimations.forEach(animation => {
				if (Features.VM[animation.animationFunction]) {
					if (animation.elementReference.animate) Features.VM[animation.animationFunction](animation.elementReference, animation.animationOptions);
					else console.warn(`WARNING :: The animation ${animation.animationFunction}() is an unsuported feature in current browser`);
				}
				else {
					throw `Undefined animation (${animation.animationFunction}) on Element [${animation.elementReference.outerHTML}]`;
				}
			});
		}
	},
};